import { useState } from "react";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormControl from "@mui/material/FormControl";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import InputAdornment from "@mui/material/InputAdornment";
import Input from "@/components/input";
import handleVariableAmountChange from "@/utils/handleVariableAmountChange";
import { useCartContext } from "@/context/CartContext";
import { AddMaxValueButton } from "@/components/AddMaxValueButton";
import Button from "@/components/button";
import convertToPoints from "@/utils/convertToPoints";
import CardImage from "@/components/cardImage";
import { useNavigate, useParams } from "react-router-dom";
import { useTravelPackagesContext } from "@/context/TravelPackagesContext";
import { TravelProduct } from "@t3/shared";
import { BRAND_ITEM_FIXED_VALUE, BRAND_ITEM_VARIABLE_VALUE } from "@/const";

export const TravelCardInfo = () => {
  const { brandKey } = useParams();
  const [variableValue, setVariableValue] = useState("");
  const [utid, setUtid] = useState("");
  const [inputWarning, setInputWarning] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const { addTravelItem } = useCartContext();
  const { packages } = useTravelPackagesContext();
  const navigate = useNavigate();

  const card = packages.find(
    (p: TravelProduct) => p.metadata.brandKey === brandKey,
  );

  const hasVariableValueItem = card?.metadata.items?.find(
    (item: any) => item.valueType === BRAND_ITEM_VARIABLE_VALUE,
  );
  const hasFixedValueItem = card?.metadata.items?.find(
    (item: any) => item.valueType === BRAND_ITEM_FIXED_VALUE,
  );
  const hasOnlyVariableValue =
    hasVariableValueItem && card?.metadata.items?.length === 1;
  const selectedItem = card?.metadata.items?.find(
    (item: any) => item.utid === utid,
  );
  if (!card) {
    throw new Error("Card not found");
  }

  const isValidSelection = () => {
    if (hasOnlyVariableValue) {
      return variableValue && Number(variableValue) > 0 && !inputWarning;
    }

    if (hasFixedValueItem) {
      if (!utid) return false;

      if (selectedItem?.valueType === BRAND_ITEM_VARIABLE_VALUE) {
        return variableValue && Number(variableValue) > 0 && !inputWarning;
      }

      return true;
    }

    return false;
  };

  const handleSubmit = async () => {
    if (!isValidSelection()) {
      return;
    }

    setSubmitting(true);
    const submittedUtid = hasOnlyVariableValue
      ? card.metadata.items[0].utid
      : utid;

    try {
      await addTravelItem({
        utid: submittedUtid,
        amount: Number(variableValue) || selectedItem?.faceValue || 0,
        type: BRAND_ITEM_FIXED_VALUE,
        metadata: card.metadata,
      });
      navigate("/shop");
    } catch (error) {
      setSubmitting(false);
    }
  };

  const handleSetUtid = (e: any) => {
    setVariableValue("");
    setInputWarning("");
    setUtid(e.target.value);
  };

  const handleAddMaxAffordable = async (amount: number) => {
    setSubmitting(true);
    const variableItem: any = card.metadata.items.find(
      (item: any) => item.valueType === BRAND_ITEM_VARIABLE_VALUE,
    );

    if (!variableItem) throw new Error("Variable item not found");

    await addTravelItem({
      utid: variableItem.utid,
      amount: amount,
      type: BRAND_ITEM_FIXED_VALUE,
      metadata: card.metadata,
    });
    navigate("/shop");
  };

  const renderVariableInput = (
    item: any,
    onClick: () => void | null = () => null,
  ) => {
    return (
      <div>
        <Input
          onClick={onClick}
          error={Boolean(inputWarning)}
          margin="normal"
          id="variable-input"
          label="Amount"
          name="amount"
          onChange={handleVariableAmountChange(
            item.minValue,
            item.maxValue,
            setVariableValue,
            setInputWarning,
          )}
          data-cypress="travel-amount-input"
          value={variableValue}
          helperText={inputWarning}
          InputProps={{
            startAdornment: <InputAdornment position="start">$</InputAdornment>,
          }}
        />
        <div>Points - {Math.round(Number(variableValue) * 5)}</div>
      </div>
    );
  };

  const renderItems = () => {
    if (hasFixedValueItem) {
      return (
        <FormControl>
          <RadioGroup
            aria-labelledby="demo-controlled-radio-buttons-group"
            name="controlled-radio-buttons-group"
            value={utid}
            onChange={handleSetUtid}
          >
            {card.metadata.items
              .sort((a: any, b: any) => a.faceValue - b.faceValue)
              .map((item: any) => {
                if (item.valueType === BRAND_ITEM_VARIABLE_VALUE) {
                  return (
                    <div key={item.utid} style={{ marginBottom: 8 }}>
                      <FormControlLabel
                        value={item.utid}
                        control={<Radio color="secondary" />}
                        label={renderVariableInput(item, () =>
                          setUtid(item.utid),
                        )}
                      />
                    </div>
                  );
                } else if (item.valueType === BRAND_ITEM_FIXED_VALUE) {
                  return (
                    <div key={item.utid} style={{ marginBottom: 8 }}>
                      <FormControlLabel
                        value={item.utid}
                        control={<Radio color="secondary" />}
                        label={`$${item.faceValue} - ${item.rewardName} - (${convertToPoints(
                          item.faceValue,
                        )} Points)`}
                      />
                    </div>
                  );
                } else {
                  return null;
                }
              })}
          </RadioGroup>
        </FormControl>
      );
    }

    if (hasOnlyVariableValue) {
      const item = card.metadata.items[0];
      return renderVariableInput(item);
    }

    return (
      <div style={{ color: "red" }}>This brand is currently not supported</div>
    );
  };

  const maxValue =
    (
      card.metadata.items.find(
        (item: any) => item.valueType === BRAND_ITEM_VARIABLE_VALUE,
      ) as any
    )?.maxValue ?? 0;

  if (card) {
    return (
      <Box data-cypress="travel-card-info-page" sx={{ flexGrow: 1 }}>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 6 }}>
            <div data-cypress="travel-card-details">
              <CardImage
                imageUrl={card.metadata.imageUrls.firebaseImage}
                height={"190px"}
                width={"300px"}
                margin={{ xs: "5px 5px 0 3px", sm: "auto" }}
              />
              <div style={{ fontWeight: "bold" }}>
                {card.metadata.brandName}
              </div>
              <div
                style={{ marginTop: 30 }}
                dangerouslySetInnerHTML={{ __html: card.metadata.description }}
              ></div>
              <div
                style={{ marginTop: 30 }}
                dangerouslySetInnerHTML={{ __html: card.metadata.disclaimer }}
              ></div>
            </div>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <div data-cypress="travel-card-purchase-options">
              <div style={{ fontWeight: "bold", marginBottom: 6 }}>
                Select Amount
              </div>
              {hasVariableValueItem && (
                <AddMaxValueButton
                  maxValue={maxValue}
                  onAdd={handleAddMaxAffordable}
                  disabled={submitting}
                />
              )}
              {renderItems()} <div style={{ marginTop: 30 }}></div>
              <Button
                data-cypress="add-to-cart-button"
                onClick={handleSubmit}
                sx={{ color: "white" }}
                disabled={submitting || !isValidSelection()}
              >
                Add To Cart
              </Button>
            </div>
          </Grid>
        </Grid>
      </Box>
    );
  }

  return null;
};
